// ─────────────────────────────────────────────────────────────
// NotificationsPanel — Sunday Triggers push notification manager
// Sits between the Browser panel and Song Info panel in the DAW.
// Uses Clerk JWT (already active) for all API calls — no separate login.
// ─────────────────────────────────────────────────────────────

import css from "../st-panel/STPanelOverlay.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {Lifecycle} from "@opendaw/lib-std"
import {NotificationsApi, NotifHistoryItem} from "./NotificationsApi"
import {CloudflareConfig} from "../cloudflare/CloudflareConfig"
import {ClerkAuth} from "../auth/ClerkAuth"

const className = Html.adoptStyleSheet(css, "NotificationsPanel")

type Construct = {lifecycle: Lifecycle}

export const NotificationsPanel = ({lifecycle}: Construct) => {

    // ── Stat display elements ─────────────────────────────────
    const deviceCountEl: HTMLElement = <span>—</span>
    const sentCountEl: HTMLElement   = <span>—</span>

    // ── Form inputs ───────────────────────────────────────────
    const titleInput = document.createElement("input")
    titleInput.className   = "st-input"
    titleInput.type        = "text"
    titleInput.placeholder = "New song available..."
    titleInput.maxLength   = 100

    const bodyInput = document.createElement("textarea")
    bodyInput.className    = "st-input"
    bodyInput.placeholder  = "We just added a new praise break to the library..."
    bodyInput.maxLength    = 200
    bodyInput.rows         = 3
    bodyInput.style.resize = "vertical"

    const linkInput = document.createElement("input")
    linkInput.className   = "st-input"
    linkInput.type        = "url"
    linkInput.placeholder = "https://youtube.com/live/... (optional)"

    const sendBtn = document.createElement("button")
    sendBtn.className     = "st-btn primary"
    sendBtn.textContent   = "Send to All Devices"
    sendBtn.disabled      = true
    sendBtn.style.cssText = "width:100%; padding:0.5rem; justify-content:center; margin-top:0.25rem"

    const statusEl: HTMLElement = <div style={{
        fontSize:  "0.65rem",
        textAlign: "center",
        marginTop: "0.4rem",
        minHeight: "1em",
    }}/>

    const historyEl: HTMLElement = <div/>

    // ── Image drop zone ───────────────────────────────────────
    const imageDropZone: HTMLElement = <div className="st-img-drop-zone"/>
    let currentImageUrl: string | null = null
    let imageUploading = false

    const refreshZone = () => {
        Html.empty(imageDropZone)
        if (currentImageUrl) {
            imageDropZone.classList.add("has-image")
            const img: HTMLImageElement = <img src={currentImageUrl} className="st-img-actual" alt="Notification image"/> as HTMLImageElement
            imageDropZone.appendChild(img)
            const deleteBtn: HTMLElement = <button type="button" className="st-img-delete-btn" title="Remove image">×</button>
            deleteBtn.addEventListener("click", (e) => {
                e.stopPropagation()
                currentImageUrl = null
                refreshZone()
            })
            imageDropZone.appendChild(deleteBtn)
        } else if (imageUploading) {
            imageDropZone.classList.remove("has-image")
            imageDropZone.appendChild(<div className="st-img-label" style={{color: "var(--st-accent-bright)"}}>Uploading…</div> as Node)
        } else {
            imageDropZone.classList.remove("has-image")
            imageDropZone.appendChild(
                <div className="st-img-label">
                    Drop image here<br/>
                    <span style={{opacity: "0.5", fontSize: "0.55rem"}}>or click to browse</span>
                </div> as Node
            )
        }
    }
    refreshZone()

    const uploadImageFile = async (file: File) => {
        if (!file.type.startsWith("image/")) return
        if (imageUploading) return
        imageUploading = true
        refreshZone()

        const ext = file.name.split(".").pop() ?? "jpg"
        const key = `notif-${crypto.randomUUID()}.${ext}`
        const uploadUrl = `${CloudflareConfig.WORKER_URL.replace("/daw/projects", "/daw/audio")}/image-upload?key=${encodeURIComponent(key)}`

        try {
            const token = await ClerkAuth.getToken()
            const res = await fetch(uploadUrl, {
                method:  "PUT",
                headers: {"Authorization": `Bearer ${token}`, "Content-Type": file.type},
                body:    await file.arrayBuffer(),
            })
            if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
            const data = await res.json() as {image_url: string}
            currentImageUrl = data.image_url
        } catch {
            const errLabel: HTMLElement = <div className="st-img-label" style={{color: "#e07c3a"}}>Upload failed — try again</div>
            Html.empty(imageDropZone)
            imageDropZone.appendChild(errLabel)
            setTimeout(refreshZone, 2500)
        } finally {
            imageUploading = false
            refreshZone()
        }
    }

    // Click to browse
    const fileInput = document.createElement("input")
    fileInput.type   = "file"
    fileInput.accept = "image/*"
    fileInput.style.display = "none"
    fileInput.addEventListener("change", () => {
        const file = fileInput.files?.[0]
        if (file) uploadImageFile(file)
        fileInput.value = ""
    })
    document.body.appendChild(fileInput)
    lifecycle.own({terminate: () => fileInput.remove()})

    imageDropZone.addEventListener("click", () => { if (!currentImageUrl) fileInput.click() })
    imageDropZone.addEventListener("dragover", (e) => { e.preventDefault(); imageDropZone.classList.add("drag-over") })
    imageDropZone.addEventListener("dragleave", () => imageDropZone.classList.remove("drag-over"))
    imageDropZone.addEventListener("drop", (e) => {
        e.preventDefault()
        imageDropZone.classList.remove("drag-over")
        const file = e.dataTransfer?.files[0]
        if (file) uploadImageFile(file)
    })

    // ── State & helpers ───────────────────────────────────────
    let isSending = false

    const updateBtn = () => {
        sendBtn.disabled = !titleInput.value.trim() || !bodyInput.value.trim() || isSending
    }

    const showStatus = (msg: string, ok: boolean) => {
        statusEl.textContent = msg
        statusEl.style.color = ok ? "var(--st-accent-bright)" : "#ef4444"
    }

    titleInput.addEventListener("input", updateBtn)
    bodyInput.addEventListener("input", updateBtn)

    // ── Data loading ──────────────────────────────────────────
    const loadData = async () => {
        try {
            const [stats, history] = await Promise.all([
                NotificationsApi.getStats(),
                NotificationsApi.getHistory(),
            ])

            deviceCountEl.textContent = String(stats.tokens ?? 0)
            sentCountEl.textContent   = String(stats.notifications ?? 0)

            Html.empty(historyEl)
            if (!history.notifications?.length) {
                historyEl.appendChild(<div className="st-empty">No notifications sent yet.</div> as Node)
                return
            }
            for (const n of history.notifications) {
                historyEl.appendChild(historyItem(n))
            }
        } catch (e) {
            console.warn("[NotificationsPanel] Failed to load:", e)
        }
    }

    const historyItem = (n: NotifHistoryItem): HTMLElement => {
        const dt    = new Date(n.sent_at).toLocaleDateString("en-US", {month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"})
        const flags = `${n.image_url ? " 🖼" : ""}${n.link ? " 🔗" : ""}`
        return (
            <div className="st-catalog-item">
                <div className="st-catalog-title">{n.title}{flags}</div>
                <div className="st-catalog-meta">
                    <span style={{fontSize: "0.64rem", color: "var(--color-dark)"}}>{n.body}</span>
                </div>
                <div className="st-catalog-meta" style={{marginTop: "0.15rem"}}>
                    <span style={{fontSize: "0.58rem", color: "var(--color-dark)"}}>{dt}</span>
                    <span style={{fontSize: "0.58rem", color: "var(--st-accent-bright)", marginLeft: "auto"}}>
                        {n.recipients_count} device{n.recipients_count !== 1 ? "s" : ""}
                    </span>
                </div>
            </div>
        )
    }

    // ── Send ──────────────────────────────────────────────────
    const send = async () => {
        const title = titleInput.value.trim()
        const body  = bodyInput.value.trim()
        const link  = linkInput.value.trim() || null
        if (!title || !body || isSending) return

        isSending = true
        sendBtn.textContent  = "Sending..."
        statusEl.textContent = ""
        updateBtn()

        try {
            const result = await NotificationsApi.sendNotification({
                title,
                body,
                image_url: currentImageUrl,
                link,
            })
            showStatus(`Sent to ${result.sent} device${result.sent !== 1 ? "s" : ""}!`, true)
            titleInput.value = ""
            bodyInput.value  = ""
            linkInput.value  = ""
            currentImageUrl  = null
            refreshZone()
            await loadData()
        } catch {
            showStatus("Failed to send. Try again.", false)
        } finally {
            isSending = false
            sendBtn.textContent = "Send to All Devices"
            updateBtn()
        }
    }

    sendBtn.addEventListener("click", send)

    // ── Init + polling ────────────────────────────────────────
    loadData()
    const interval = setInterval(loadData, 30_000)
    lifecycle.own({terminate: () => clearInterval(interval)})

    // ── Stat box helper ───────────────────────────────────────
    const statBox = (label: string, valueEl: HTMLElement): HTMLElement => (
        <div style={{
            flex:         "1",
            textAlign:    "center",
            background:   "rgba(0,0,0,0.2)",
            borderRadius: "6px",
            padding:      "0.5rem 0.25rem",
        }}>
            <div style={{fontSize: "1.1rem", fontWeight: "700", color: "var(--st-accent-bright)"}}>{valueEl}</div>
            <div style={{fontSize: "0.52rem", color: "var(--color-dark)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "0.2rem"}}>{label}</div>
        </div>
    )

    // ── Render ────────────────────────────────────────────────
    return (
        <div className={className}>
            <div className="st-content">

                {/* Stats */}
                <div className="st-section" style={{paddingBottom: "0.5rem"}}>
                    <div className="st-row">
                        {statBox("Devices", deviceCountEl)}
                        {statBox("Sent", sentCountEl)}
                    </div>
                </div>

                <hr className="st-divider" style={{margin: "0"}}/>

                {/* Compose */}
                <div className="st-section">
                    <div className="st-label" style={{marginBottom: "0.75rem"}}>Send Notification</div>

                    <div className="st-field">
                        <div className="st-label">Title</div>
                        {titleInput}
                    </div>

                    <div className="st-field">
                        <div className="st-label">Message</div>
                        {bodyInput}
                    </div>

                    <div className="st-field">
                        <div className="st-label">
                            Image
                            <span style={{opacity: "0.4", textTransform: "none", letterSpacing: "0", fontWeight: "400", marginLeft: "0.3em"}}>optional</span>
                        </div>
                        {imageDropZone}
                    </div>

                    <div className="st-field">
                        <div className="st-label">
                            Link
                            <span style={{opacity: "0.4", textTransform: "none", letterSpacing: "0", fontWeight: "400", marginLeft: "0.3em"}}>optional</span>
                        </div>
                        {linkInput}
                    </div>

                    {sendBtn}
                    {statusEl}
                </div>

                <hr className="st-divider" style={{margin: "0"}}/>

                {/* History */}
                <div className="st-section" style={{paddingBottom: "0.25rem"}}>
                    <div className="st-label">History</div>
                </div>
                {historyEl}

            </div>
        </div>
    )
}
